import { API_ENDPOINTS } from '../../config/apiConfig';

export const CHROME_THEMES = [
  { id: 'slate', label: 'Slate', description: 'Cool blue-gray chrome' },
  { id: 'navy', label: 'Navy', description: 'Deep institutional blue' },
  { id: 'graphite', label: 'Graphite', description: 'Neutral zinc gray' },
  { id: 'clinical_teal', label: 'Clinical teal', description: 'Healthcare teal chrome (default)' },
];

export const ACCENT_THEMES = [
  { id: 'purple', label: 'Purple', description: 'Brand purple accent' },
  { id: 'blue', label: 'Clinical blue', description: 'Professional blue actions' },
  { id: 'indigo', label: 'Indigo', description: 'Modern indigo accent' },
  { id: 'teal', label: 'Teal', description: 'Clinical teal accent (default)' },
];

export const DEFAULT_CHROME_THEME = 'clinical_teal';
export const DEFAULT_ACCENT_THEME = 'teal';
export const ORG_BRANDING_STORAGE_KEY = 'org-branding-cache';

export const ORG_BRANDING_EVENT = 'mdm-org-branding';

const CHROME_IDS = new Set(CHROME_THEMES.map((t) => t.id));
const ACCENT_IDS = new Set(ACCENT_THEMES.map((t) => t.id));

export function isValidChromeTheme(id) {
  return CHROME_IDS.has(id);
}

export function isValidAccentTheme(id) {
  return ACCENT_IDS.has(id);
}

export function getChromeThemeLabel(id) {
  return CHROME_THEMES.find((t) => t.id === id)?.label || id;
}

export function getAccentThemeLabel(id) {
  return ACCENT_THEMES.find((t) => t.id === id)?.label || id;
}

export function normalizeOrgBranding(raw = {}) {
  const chrome = isValidChromeTheme(raw.chrome_theme)
    ? raw.chrome_theme
    : DEFAULT_CHROME_THEME;
  const accent = isValidAccentTheme(raw.accent_theme)
    ? raw.accent_theme
    : DEFAULT_ACCENT_THEME;
  return { chrome_theme: chrome, accent_theme: accent };
}

function notifyOrgBrandingApplied() {
  try {
    window.dispatchEvent(new Event(ORG_BRANDING_EVENT));
  } catch {
    /* ignore */
  }
}

export function getStoredOrgBranding() {
  try {
    const raw = localStorage.getItem(ORG_BRANDING_STORAGE_KEY);
    if (!raw) return null;
    return normalizeOrgBranding(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function setStoredOrgBranding(branding, organizationId = null) {
  const normalized = normalizeOrgBranding(branding);
  try {
    localStorage.setItem(
      ORG_BRANDING_STORAGE_KEY,
      JSON.stringify({
        ...normalized,
        organization_id: organizationId ?? null,
      })
    );
  } catch {
    /* ignore */
  }
}

export function applyOrgBranding(branding = {}) {
  const { chrome_theme: chrome, accent_theme: accent } = normalizeOrgBranding(branding);
  const root = document.documentElement;
  const unchanged =
    root.getAttribute('data-chrome-theme') === chrome
    && root.getAttribute('data-accent-theme') === accent;
  root.setAttribute('data-chrome-theme', chrome);
  root.setAttribute('data-accent-theme', accent);
  if (!unchanged) {
    notifyOrgBrandingApplied();
  }
  return { chrome_theme: chrome, accent_theme: accent };
}

export function applyOrgBrandingFromUser(user) {
  if (user?.org_branding) {
    const applied = applyOrgBranding(user.org_branding);
    setStoredOrgBranding(applied, user.organization_id ?? null);
    return applied;
  }
  const cached = getStoredOrgBranding();
  if (cached) {
    return applyOrgBranding(cached);
  }
  return applyOrgBranding({
    chrome_theme: DEFAULT_CHROME_THEME,
    accent_theme: DEFAULT_ACCENT_THEME,
  });
}

export function readOrgBrandingFromSessionUser() {
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return null;
    const user = JSON.parse(raw);
    return user?.org_branding ? normalizeOrgBranding(user.org_branding) : null;
  } catch {
    return null;
  }
}

export function updateSessionUserOrgBranding(branding) {
  const normalized = normalizeOrgBranding(branding);
  try {
    const raw = sessionStorage.getItem('user');
    if (!raw) return;
    const user = JSON.parse(raw);
    user.org_branding = normalized;
    sessionStorage.setItem('user', JSON.stringify(user));
  } catch {
    /* ignore */
  }
}

/** Public org API — guest flows with a known org id. */
export async function fetchAndApplyOrgBranding(organizationId) {
  const id = parseInt(organizationId, 10);
  if (!Number.isFinite(id) || id <= 0) return null;

  try {
    const response = await fetch(API_ENDPOINTS.ORGANIZATIONS.GET(id));
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.branding) return null;
    const applied = applyOrgBranding(data.branding);
    setStoredOrgBranding(applied, id);
    return applied;
  } catch {
    return null;
  }
}

/** Login / legal pages: backend resolves org (single-org or DEFAULT_ORG_CODE). */
export async function fetchAndApplyPublicBranding() {
  try {
    const response = await fetch(API_ENDPOINTS.ORGANIZATIONS.PUBLIC_BRANDING);
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.branding) return null;
    const applied = applyOrgBranding(data.branding);
    setStoredOrgBranding(applied, data.organization_id ?? null);
    return applied;
  } catch {
    return null;
  }
}

/** Apply branding for unauthenticated routes: cache first, then public branding API. */
export async function bootstrapOrgBrandingForPublicPages() {
  try {
    const raw = sessionStorage.getItem('user');
    const user = raw ? JSON.parse(raw) : null;
    if (user?.org_branding) {
      return applyOrgBrandingFromUser(user);
    }
  } catch {
    /* fall through */
  }

  const cached = getStoredOrgBranding();
  if (cached) {
    applyOrgBranding(cached);
  }

  const fetched = await fetchAndApplyPublicBranding();
  if (fetched) return fetched;

  return applyOrgBranding(cached || {});
}
